import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Heart, Plus, ShoppingCart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFavoriteAuth } from '../../hooks/useFavoriteAuth';
import { useCart } from '../../contexts/CartContext';
import LoginPopup from '../LoginPopup';
import DishPopup from './DishPopup';
import DishImageSplash from '../common/DishImageSplash';
import { getDishImageUrl } from '../../services/api';
import { RatingStarsFa, getDisplayRating } from '../../utils/ratingStars';

const DishCardUpdated = ({
  dish,
  onAddToCart,
  onFavoriteToggle,
  onCardClick,
  onPlusClick,
  showRestaurantInfo = true,
  className = "",
  forceFavoriteState = null // null = auto-check, true/false = forced state
}) => {
  const { t } = useTranslation();
  const [isFavorite, setIsFavorite] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [liveRating, setLiveRating] = useState(getDisplayRating(dish, 0));
  const [liveReviewCount, setLiveReviewCount] = useState(dish?.review_count ?? 0);
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const {
    isAuthenticated,
    checkFavoriteStatus,
    handleFavoriteAction,
    loading: authFavoriteLoading,
    showLoginPopup,
    closeLoginPopup,
    handleLoginSuccess
  } = useFavoriteAuth();

  // Vérifier l'état des favoris au montage
  useEffect(() => {
    const checkFavorite = async () => {
      if (forceFavoriteState !== null) {
        // État forcé
        setIsFavorite(forceFavoriteState);
        return;
      }

      if (isAuthenticated && dish?.id) {
        try {
          setFavoriteLoading(true);
          const status = await checkFavoriteStatus(dish.id, 'dish');
          setIsFavorite(status);
        } catch (error) {
          console.error('Error checking favorite status:', error);
        } finally {
          setFavoriteLoading(false);
        }
      }
    };

    checkFavorite();
  }, [dish?.id, isAuthenticated, checkFavoriteStatus, forceFavoriteState]);

  useEffect(() => {
    setLiveRating(getDisplayRating(dish, 0));
    setLiveReviewCount(dish?.review_count ?? 0);
  }, [dish]);

  useEffect(() => {
    const onDishReviewUpdated = (event) => {
      const detail = event?.detail || {};
      if (!dish?.id || Number(detail.dishId) !== Number(dish.id)) return;
      if (typeof detail.rating === 'number') {
        setLiveRating(detail.rating);
      }
      if (typeof detail.reviewCount === 'number') {
        setLiveReviewCount(detail.reviewCount);
      }
    };
    window.addEventListener('afrodine:dish-review-updated', onDishReviewUpdated);
    return () => {
      window.removeEventListener('afrodine:dish-review-updated', onDishReviewUpdated);
    };
  }, [dish?.id]);

  // Gérer l'ouverture du popup
  const handlePlusClick = (e) => {
    e.stopPropagation();
    setShowPopup(true);
    if (onPlusClick) {
      onPlusClick(dish);
    }
  };

  // Gérer l'ajout au panier depuis le popup
  const handleAddToCartFromPopup = (cartItem) => {
    if (onAddToCart) {
      onAddToCart(cartItem);
    } else {
      addToCart(cartItem);
    }
    setShowPopup(false);
  };

  // Gérer l'ajout au panier direct (ancien comportement)
  const handleAddToCart = async (e) => {
    e.stopPropagation();
    
    if (onAddToCart) {
      setIsAddingToCart(true);
      try {
        await onAddToCart(dish);
        
        // Animation de succès
        const button = e.target.closest('button');
        if (button) {
          button.classList.add('bg-green-500');
          setTimeout(() => {
            button.classList.remove('bg-green-500');
            button.classList.add('bg-green-600');
          }, 200);
        }
      } catch (error) {
        console.error('Erreur lors de l\'ajout au panier:', error);
      } finally {
        setIsAddingToCart(false);
      }
    }
  };

  // Gérer le toggle des favoris (API via useFavoriteAuth si pas de callback parent — ex. page d'accueil)
  const handleFavoriteToggle = async (e) => {
    e.stopPropagation();
    if (!dish?.id) return;

    if (onFavoriteToggle) {
      if (!isAuthenticated) return;
      const newFavoriteState = !isFavorite;
      setIsFavorite(newFavoriteState);
      setFavoriteLoading(true);
      try {
        await onFavoriteToggle(dish.id, newFavoriteState);
      } catch (error) {
        setIsFavorite(!newFavoriteState);
        console.error('Error toggling favorite:', error);
      } finally {
        setFavoriteLoading(false);
      }
      return;
    }

    const action = isFavorite ? 'remove' : 'add';
    try {
      const success = await handleFavoriteAction(action, dish.id, 'dish');
      if (success) {
        setIsFavorite(!isFavorite);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const guestLocked = onFavoriteToggle && !isAuthenticated;
  const favoriteBusy = favoriteLoading || authFavoriteLoading;

  // Gérer le clic sur la carte
  const handleCardClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/product/${dish.id}`);
  };

  // Formater le prix
  const formatPrice = (price) => {
    return `${(price || 0).toFixed(2)} €`;
  };

  // Obtenir l'URL de l'image
  const getImageUrl = () => {
    return getDishImageUrl(dish) || '/default-dish.jpg';
  };
  const displayRating = liveRating;

  return (
    <>
    <div 
      className={`bg-white rounded-xl transition-all duration-300 cursor-pointer group overflow-hidden ${className}`}
      onClick={handleCardClick}
    >
      {/* Image du plat */}
      <div className="relative h-48 overflow-hidden">
        <DishImageSplash
          src={getImageUrl()}
          alt={dish.name}
          category={dish.category?.name}
          dishName={dish.name}
          className="w-full h-full"
          fallbackEmoji="🍽️"
        />
        
        {/* Badge de favori */}
        <button
          type="button"
          onClick={handleFavoriteToggle}
          disabled={guestLocked || favoriteBusy}
          className={`absolute top-3 right-3 p-2 rounded-full transition-all duration-200 ${
            isFavorite
              ? 'bg-red-500 text-white hover:bg-red-600'
              : guestLocked
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-white/90 text-gray-600 hover:bg-red-50 hover:text-red-500'
          } ${favoriteBusy ? 'animate-pulse' : ''}`}
          title={
            guestLocked
              ? t('restaurant.loginToFavorite')
              : isFavorite
                ? t('home.removeFavorite')
                : t('home.addFavorite')
          }
        >
          {favoriteBusy ? (
            <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <Heart
              className={`h-4 w-4 ${isFavorite ? 'fill-current' : ''}`}
            />
          )}
        </button>
        
        {/* Badge de promotion si applicable */}
        {(dish.discount_percentage || dish.is_promoted) && (
          <div className="absolute top-3 left-3 px-2 py-1 rounded bg-red-600 text-white text-[11px] font-bold">
            {dish.discount_percentage ? `-${dish.discount_percentage}%` : t('home.promo')}
          </div>
        )}
      </div>
      
      {/* Contenu de la carte */}
      <div className="p-4">
        {/* Nom du plat */}
        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-green-600 transition-colors">
          {dish.name}
        </h3>
        
        {/* Description */}
        {dish.description && (
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
            {dish.description}
          </p>
        )}
        
        {/* Note et avis */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center">
            <RatingStarsFa rating={displayRating} size="sm" />
          </div>
          <span className="text-sm text-gray-600">
            {displayRating > 0 ? displayRating.toFixed(1) : '0.0'}
          </span>
          {Number(liveReviewCount) > 0 && (
            <span className="text-xs text-gray-500">
              {t('restaurant.reviewCountParens', { count: liveReviewCount })}
            </span>
          )}
        </div>
        
        {/* Informations du restaurant */}
        {showRestaurantInfo && dish.restaurant && (
          <div className="text-xs text-gray-500 mb-3">
            <span className="font-medium">{dish.restaurant.name}</span>
            {dish.restaurant.city && (
              <span className="ml-1">• {dish.restaurant.city}</span>
            )}
          </div>
        )}
        
        {/* Prix et bouton d'ajout */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {dish.discount_percentage ? (
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold" style={{ color: 'rgb(5, 70, 37)' }}>
                  {formatPrice(dish.discounted_price || dish.price * (1 - dish.discount_percentage / 100))}
                </span>
                <span className="text-sm text-gray-500 line-through">
                  {formatPrice(dish.price)}
                </span>
              </div>
            ) : (
              <span className="text-lg font-bold" style={{ color: 'rgb(5, 70, 37)' }}>
                {formatPrice(dish.price)}
              </span>
            )}
          </div>
          
          <button
            onClick={handlePlusClick}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 shadow-lg"
            style={{
              backgroundColor: 'black',
              color: 'white'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgb(31, 31, 31)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'black';
            }}
          >
            <Plus size={20} />
          </button>
        </div>
        
        {/* Informations supplémentaires */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between text-xs text-gray-500">
            {dish.preparation_time && (
              <span>⏱️ {t('restaurant.minutes', { count: dish.preparation_time })}</span>
            )}
            {dish.is_vegetarian && (
              <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full">
                {t('restaurant.dietary.vegetarian')}
              </span>
            )}
            {dish.is_vegan && (
              <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full">
                {t('restaurant.dietary.vegan')}
              </span>
            )}
            {dish.is_gluten_free && (
              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                {t('restaurant.dietary.glutenFree')}
              </span>
            )}
            {dish.is_halal && (
              <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                {t('restaurant.dietary.halal')}
              </span>
            )}
          </div>
        </div>
      </div>

    </div>
    <DishPopup
      dish={dish}
      isOpen={showPopup}
      onClose={() => setShowPopup(false)}
      onAddToCart={handleAddToCartFromPopup}
    />
    {!onFavoriteToggle && (
      <LoginPopup
        isOpen={showLoginPopup}
        onClose={closeLoginPopup}
        onLoginSuccess={handleLoginSuccess}
      />
    )}
    </>
  );
};

export default DishCardUpdated;
